import axios, { AxiosHeaders, AxiosRequestConfig } from 'axios';
import { ReactNode, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import * as Sentry from '@sentry/react';

import { ERROR_TOAST_BY_CODE } from '@components/Toast/consts';
import { envInfo } from '@constants/constants';
import { SUPPRESSED_AUTH_ERROR_ROUTES } from '@constants/routes';
import type { ToastItem } from '@customTypes/toast';
import { useKeycloak } from '@hooks/useKeycloak';
import useToast from '@hooks/useToast';
import { clearUser } from '@utils/openreplay';

const instance = axios.create({
  baseURL: envInfo.apiUrl,
  validateStatus: status => status < 400,
  timeout: 10000,
});

export const updateAxiosToken = (token: string | undefined) => {
  if (token) {
    instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete instance.defaults.headers.common.Authorization;
  }
};

export const AxiosInterceptor = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { keycloak } = useKeycloak();
  const { addToast } = useToast();

  // Use refs to avoid recreating interceptors when keycloak/addToast change
  const keycloakRef = useRef(keycloak);
  const addToastRef = useRef(addToast);
  const pathnameRef = useRef(pathname);

  // Update refs when values change
  keycloakRef.current = keycloak;
  addToastRef.current = addToast;
  pathnameRef.current = pathname;

  useEffect(() => {
    // Request interceptor to automatically add auth header
    const requestInterceptor = instance.interceptors.request.use(
      config => {
        const currentKeycloak = keycloakRef.current;
        if (currentKeycloak?.token && currentKeycloak?.authenticated && config.headers) {
          // Just use the current token - let keycloak hook handle refresh
          (config.headers as AxiosHeaders).Authorization = `Bearer ${currentKeycloak.token}`;
        }
        return config;
      },
      error => Promise.reject(error),
    );

    const responseInterceptor = instance.interceptors.response.use(
      response => response,
      error => {
        // Allow requests to opt-out from global error handling/toast
        const cfg: AxiosRequestConfig & {
          skipResponseInterceptor?: boolean;
          suppressGlobalErrorToast?: boolean;
        } = (error?.config as AxiosRequestConfig) || {};

        let shouldSuppressToast = cfg?.suppressGlobalErrorToast;
        if (!shouldSuppressToast) {
          if (SUPPRESSED_AUTH_ERROR_ROUTES.some(p => matchPath(p, pathnameRef.current))) {
            shouldSuppressToast = true;
          }
        }

        if (cfg?.skipResponseInterceptor || shouldSuppressToast) {
          return Promise.reject(error);
        }

        const statusCode: number | undefined = error?.response?.status;
        const currentKeycloak = keycloakRef.current;

        // Handle 401 errors specially - don't show toast, let keycloak handle it
        if (statusCode === 401) {
          if (currentKeycloak?.authenticated) {
            // Token is invalid, trigger logout
            console.warn('401 error received, triggering logout');
            clearUser();
            currentKeycloak.logout();
          }
          return Promise.reject(error);
        }

        // Handle axios request cancellation
        if (axios.isCancel(error)) {
          console.log('Request was cancelled');
          return Promise.reject(error);
        }

        // Send to sentry for 5xx
        const code = statusCode ?? 0;
        if (code >= 500) {
          Sentry.captureException(error);
        }

        // Build toast to show to user
        const predefined = statusCode ? ERROR_TOAST_BY_CODE[statusCode] : undefined;
        const messageFromBackend: string | undefined =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message;

        const toastConfig: Omit<ToastItem, 'id'> = predefined
          ? {
              ...predefined,
              content: messageFromBackend || predefined.content, // Use backend message if available
              autohide: false,
            }
          : {
              variant: 'danger',
              content: messageFromBackend,
              autohide: false,
            };

        addToastRef.current(toastConfig);

        return Promise.reject(error);
      },
    );

    return () => {
      instance.interceptors.request.eject(requestInterceptor);
      instance.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return <>{children}</>;
};

export default instance;
